import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAIUsage } from '../../hooks/useAIUsage';
import { supabase } from '../../lib/supabase';

const MonthlyUsageCard = () => {
  const navigate = useNavigate();
  const { userPlan, loading: planLoading } = useAIUsage();
  const [totalUsed, setTotalUsed] = useState(0);
  const [totalLimit, setTotalLimit] = useState(0);
  const [resetDate, setResetDate] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsageData = async () => {
      try {
        setLoading(true);
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user?.id) return;

        // Get current month usage
        const currentMonth = new Date().toISOString().substring(0, 7); // YYYY-MM format
        const { data: usageData, error: usageError } = await supabase
          .from('ai_usage_tracking')
          .select('*')
          .eq('user_id', user.id)
          .eq('usage_month', currentMonth)
          .single();

        if (usageError && usageError.code !== 'PGRST116') {
          console.error('Error fetching usage data:', usageError);
          return;
        }

        // Calculate total usage and limits
        let used = 0;
        let limit = 0;

        if (usageData) {
          used = (usageData.recipe_generations_used || 0) + 
                 (usageData.image_scans_used || 0) + 
                 (usageData.meal_analysis_used || 0);
          
          // Format reset date
          if (usageData.next_reset_date) {
            const nextReset = new Date(usageData.next_reset_date);
            setResetDate(nextReset.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            }));
          }
        }

        // Get limits from user plan
        if (userPlan) {
          limit = (userPlan.ai_recipe_generation_limit || 0) + 
                  (userPlan.ai_image_scans_limit || 0) + 
                  (userPlan.ai_meal_analysis_limit || 0);
        } else {
          // Default free plan limits
          limit = 30; // 10 + 10 + 10
        }

        setTotalUsed(used);
        setTotalLimit(limit);
      } catch (error) {
        console.error('Error in fetchUsageData:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!planLoading) {
      fetchUsageData();
    }
  }, [userPlan, planLoading]);

  if (loading || planLoading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
          <div className="flex justify-between">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
          </div>
        </div>
      </div>
    );
  }

  const usageProgress = totalLimit > 0 ? totalUsed / totalLimit : 0;
  const planType = userPlan?.plan_type || 'free';

  const handleClick = () => {
    // Navigate to profile page with subscription tab
    navigate('/profile?tab=subscription');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
      className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Monthly Usage
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
          {planType} Plan
        </span>
      </div>
      
      {/* Progress Bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 mb-3">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(usageProgress * 100, 100)}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className={`h-2 rounded-full ${
            usageProgress >= 1 
              ? 'bg-red-500' 
              : usageProgress >= 0.8 
                ? 'bg-yellow-500' 
                : 'bg-primary'
          }`}
        />
      </div>
      
      {/* Usage Details */}
      <div className="flex justify-between text-xs">
        <span className="text-gray-600 dark:text-gray-400">
          {totalUsed} / {totalLimit} used
        </span>
        <span className="text-gray-600 dark:text-gray-400">
          {resetDate && `Resets ${resetDate}`}
        </span>
      </div>
    </motion.div>
  );
};

export default MonthlyUsageCard;